// Lib
import React, { useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Popover, UtilityButton } from '@milanote/roma';
import { cx } from '@milanote/roma/utils';
import { useTranslator } from '../../../../components/i18n/useTranslator';

// Utils
import { getStrokeColorWithFallback } from '../drawingEditorUtils';
import { stopDefaultAndPropagation } from '../../../../utils/domUtil';
import { setDrawingEditorColorToolTempColorStyles } from './DrawingEditorColorTool';

// Components
import CustomColorInput from '../../../../workspace/toolbar/components/selectionTools/colorTools/CustomColorInput';
import ColorPopupContent from '../../../../workspace/toolbar/components/selectionTools/colorTools/popup/ColorPopupContent';

// Selectors
import currentBoardSuggestedColorsSelector from '../../../../workspace/toolbar/components/selectionTools/colorTools/currentBoardSuggestedColorsSelector';

// Hooks
import useTouchBlockedPointerEvent from './useTouchBlockedPointerEvent';

// Types
import type { Color } from '../../../../../common/colors/colorConstants';

// Styles
import './DrawingEditorColorTool.scss';

type DrawingEditorColorToolV2Props = {
    isPenMode: boolean;
    disabled: boolean;
    keepPopupOpen: boolean;
    strokeColor: string;
    defaultColors: Color[];
    colorPopupOpen: boolean;
    // Accepts null: the "None" swatch (COLORS.NONE) commits null, which resolves to the fallback colour.
    setStrokeColor: (color: string | null) => void;
    setColorPopupOpen: (isOpen: boolean) => void;
    setCustomColorInputOpen: (isOpen: boolean) => void;
};

/**
 * Roma-styled drawing colour picker trigger for the V2 drawing toolbar.
 */
const DrawingEditorColorToolV2: React.FC<DrawingEditorColorToolV2Props> = ({
    isPenMode,
    disabled,
    keepPopupOpen,
    strokeColor,
    defaultColors,
    colorPopupOpen,

    setStrokeColor,
    setColorPopupOpen,
    setCustomColorInputOpen,
}) => {
    const triggerRef = useRef<HTMLButtonElement>(null);
    const customColorInputRef = useRef<CustomColorInput>(null);
    const suggestedColors = useSelector(currentBoardSuggestedColorsSelector);
    const translate = useTranslator('popup', 'drawing');
    const displayStrokeColor = getStrokeColorWithFallback(strokeColor);

    const togglePopup = useCallback(() => setColorPopupOpen(!colorPopupOpen), [setColorPopupOpen, colorPopupOpen]);
    const onButtonPointerUp = useTouchBlockedPointerEvent(isPenMode, togglePopup);

    const closePopup = () => setColorPopupOpen(false);

    const openCustomColorPicker = useCallback(() => {
        customColorInputRef.current?.open();
    }, []);

    const setColorAndClose = (color: string | null) => {
        // Pass the value through even when it's null: the top-left "None" swatch commits null on purpose,
        // and setStrokeColor resolves it to the fallback colour. Guarding on truthiness here silently drops
        // that click, which is the whole swatch's behaviour. The custom-colour input never emits null.
        setStrokeColor(color);
        !keepPopupOpen && closePopup();
    };

    return (
        <div className="DrawingEditorColorTool" onDoubleClick={stopDefaultAndPropagation}>
            <UtilityButton
                ref={triggerRef}
                isDisabled={disabled}
                isSelected={colorPopupOpen}
                className="DrawingEditorColorButton"
                onPointerUp={!disabled ? onButtonPointerUp : undefined}
                onDoubleClick={stopDefaultAndPropagation}
                aria-label={translate('tools.color')}
                tooltip={{ title: translate('tools.color') }}
            >
                <span
                    className="color-target size-[18px] shrink-0 rounded-xs border border-black_alpha"
                    style={{ backgroundColor: displayStrokeColor }}
                    aria-hidden
                />
            </UtilityButton>

            <CustomColorInput
                className={cx('size-0', colorPopupOpen && 'is-open')}
                ref={customColorInputRef}
                onFocus={() => setCustomColorInputOpen(true)}
                onBlur={() => setCustomColorInputOpen(false)}
                initialColor={displayStrokeColor}
                setTempColor={setDrawingEditorColorToolTempColorStyles}
                setColor={setColorAndClose}
            />

            <Popover
                triggerRef={triggerRef}
                isOpen={colorPopupOpen}
                onOpenChange={setColorPopupOpen}
                placement="top"
                popoverClassName="DrawingEditorColorPopup color-popup"
            >
                <ColorPopupContent
                    defaultColors={defaultColors}
                    suggestedColors={suggestedColors}
                    selectedColor={strokeColor}
                    closePopup={closePopup}
                    openCustomColorPicker={openCustomColorPicker}
                    setColor={setColorAndClose}
                />
            </Popover>
        </div>
    );
};

export default DrawingEditorColorToolV2;
